import { format, isValid, parse } from 'date-fns';

import { pluralize } from './helpers';

export const ONE_DAY_MS = 86_400_000;

export const getUTCDate = (date: string | Date) => {
  const zonedDate = typeof date === 'string' ? new Date(date) : date;

  return new Date(zonedDate.getUTCFullYear(), zonedDate.getUTCMonth(), zonedDate.getUTCDate());
};

export const getDatePart = (date: string | Date) => {
  const zonedDate = typeof date === 'string' ? new Date(date) : date;

  return new Date(zonedDate.getFullYear(), zonedDate.getMonth(), zonedDate.getDate());
};

export const convertTimeZone = (date: string | Date, timeZone: string) => {
  const zonedDate = typeof date === 'string' ? new Date(date) : date;

  return new Date(zonedDate.toLocaleString('en-US', { timeZone }));
};

const LeaseEndTimeZone = 'America/Denver';

export const convertToLeaseEndTimeZone = (date: string | Date) =>
  convertTimeZone(date, LeaseEndTimeZone);

export const formatDateISO = (date: Date) => date.toISOString().split('T')[0];

export const dateOrdinal = (number: number) => {
  if (number > 3 && number < 21) return 'th';
  switch (number % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

export const checkLastDayOfMonth = () => {
  const date = new Date();
  const dayOfMonth = date.getDate();
  const month = date.getMonth();
  const year = date.getFullYear();
  const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);

  const lastDaysOfMonths = {
    0: 31,
    1: isLeapYear ? 29 : 28,
    2: 31,
    3: 30,
    4: 31,
    5: 30,
    6: 31,
    7: 31,
    8: 30,
    9: 31,
    10: 30,
    11: 31,
  };

  return dayOfMonth === lastDaysOfMonths[month as keyof typeof lastDaysOfMonths];
};

export const getDay = (date: Date) => date.getDate();

export const getMonth = (date: Date, monthFormat?: Intl.DateTimeFormatOptions['month']) =>
  new Intl.DateTimeFormat('en-US', { month: monthFormat ?? 'long' }).format(date);

export const addMonthsToDate = (months: number, date = new Date()) => {
  const newDate = new Date(date.getTime());
  newDate.setMonth(newDate.getMonth() + months);
  return newDate;
};

export const getFutureDate = (days: number) => {
  const todayDate = new Date();

  todayDate.setDate(todayDate.getDate() + days);

  const futureMonth = new Intl.DateTimeFormat('en-US', { month: 'long' })
    .format(todayDate)
    .toLocaleUpperCase();
  const futureDay = todayDate.getDate();

  return {
    futureDay,
    futureMonth,
  };
};

export const parseAndFormatDate = (dateString?: string | null, targetFormat = 'yyyy-MM-dd') => {
  if (!dateString) {
    return '';
  }

  const possibleFormats = ['yyyy-MM-dd', 'MM/dd/yyyy'];
  // eslint-disable-next-line no-restricted-syntax
  for (const possibleFormat of possibleFormats) {
    const parsedDate = parse(dateString, possibleFormat, new Date());
    const isValidDate = isValid(parsedDate);

    if (isValidDate) {
      return format(parsedDate, targetFormat);
    }
  }

  return '';
};

export const relativeFormat = (date: Date | string): string => {
  const targetDate = typeof date === 'string' ? new Date(date) : date;
  if (!(targetDate instanceof Date) || Number.isNaN(targetDate.getTime())) return '';

  const now = new Date();
  let diffMs = now.getTime() - targetDate.getTime();
  const future = diffMs < 0;
  if (future) diffMs = -diffMs;

  const MINUTE_MS = 60_000;
  const HOUR_MS = 3_600_000;

  const minutes = Math.floor(diffMs / MINUTE_MS);
  const hours = Math.floor(diffMs / HOUR_MS);
  const days = Math.floor(diffMs / ONE_DAY_MS);
  const weeks = Math.floor(days / 7);

  const unit = (n: number, s: string) => `${n} ${s}${n === 1 ? '' : 's'}`;
  const fmt = (s: string) => (future ? `in ${s}` : `${s} ago`);

  if (minutes < 1) return future ? 'in less than 1 minute' : 'just now';
  if (minutes < 60) return fmt(unit(minutes, 'minute'));
  if (hours < 24) return fmt(unit(hours, 'hour'));
  if (days <= 13) return fmt(unit(days, 'day'));
  return fmt(unit(weeks, 'week'));
};

export function getISOWithOffset(date: Date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    throw new Error('Invalid date provided to getISOWithOffset');
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  const tzOffset = -date.getTimezoneOffset(); // in minutes
  const sign = tzOffset >= 0 ? '+' : '-';
  const offsetHours = pad(Math.floor(Math.abs(tzOffset) / 60));
  const offsetMinutes = pad(Math.abs(tzOffset) % 60);

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}:${pad(date.getSeconds())}${sign}${offsetHours}:${offsetMinutes}`;
}

export const formatDateToString = (date: Date | string, dateFormat = 'MMMM dd, yyyy') => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (!isValid(dateObj)) return '';
  return format(dateObj, dateFormat);
};

export const formatStageTimestamp = (date?: string | Date | null): string => {
  if (!date) return '';
  const target = typeof date === 'string' ? new Date(date) : date;
  if (!isValid(target)) return '';

  const startOfDay = (value: Date) =>
    new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(new Date()) - startOfDay(target)) / ONE_DAY_MS);
  const time = format(target, 'h:mm a');

  if (dayDiff === 0) return `Today · ${time}`;
  if (dayDiff === 1) return `Yesterday · ${time}`;
  if (dayDiff > 1 && dayDiff < 7) return `${format(target, 'EEE')} · ${time}`;
  return format(target, 'MMM d');
};

// Local-day key (YYYY-M-D in the device's timezone) for grouping messages under day dividers.
// Day dividers are decided by the driver's local clock, not a business timezone.
export const localDayKey = (createdAt: string) => {
  const date = new Date(createdAt);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

// The bubble's send time, e.g. "4:45 PM", in the driver's local time.
export const formatMessageTime = (createdAt: string) => format(new Date(createdAt), 'h:mm a');

const startOfLocalDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

// The day-divider label in local time: "TODAY" / "YESTERDAY", otherwise the weekday with month
// and day ("Mon, Jun 15"). The year is shown only when the message is from a prior calendar year
// ("Jun 15, 2025").
export const formatDayDivider = (createdAt: string) => {
  const date = new Date(createdAt);
  const now = new Date();
  const dayDiff = Math.round((startOfLocalDay(now) - startOfLocalDay(date)) / ONE_DAY_MS);

  if (dayDiff === 0) return 'TODAY';
  if (dayDiff === 1) return 'YESTERDAY';
  if (date.getFullYear() !== now.getFullYear()) return format(date, 'MMM d, yyyy');
  return format(date, 'EEE, MMM d');
};

export const parseValidDate = (dateStr?: string | null): Date | undefined => {
  if (!dateStr) return undefined;
  const date = new Date(dateStr);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

export const formatExpiresIn = (
  expiresAt?: string | null,
): { label: string; isUrgent: boolean } | null => {
  const target = parseValidDate(expiresAt);
  if (target == null) return null;

  const remainingMs = target.getTime() - Date.now();
  const HOUR_MS = 3_600_000;
  const hours = remainingMs / HOUR_MS;
  const isUrgent = hours < 6;

  // Past the timestamp but the server still says pending: floor the label rather than vanish.
  if (hours < 1) {
    const minutes = Math.floor(remainingMs / 60_000);
    return { label: minutes < 1 ? 'EXPIRES IN <1 MIN' : `EXPIRES IN ${minutes} MIN`, isUrgent };
  }
  if (hours < 24) return { label: `EXPIRES IN ${Math.floor(hours)} HR`, isUrgent };
  const days = Math.floor(hours / 24);
  return { label: `EXPIRES IN ${days} ${pluralize('DAY', days, 'S')}`, isUrgent };
};
